/**
 * Empty-state launcher for the right sidebar.
 *
 * Shown when the panel is open with no surface attached. Every surface Forge
 * can host gets a row, including the ones that are currently unavailable,
 * which stay visible with the reason instead of disappearing, so the panel
 * answers "what can go here?" rather than "why is this empty?".
 *
 * Keyboard model is Tab and Enter on real buttons, plus arrows to roam the
 * list. Deliberately no bare-letter accelerator: Forge focuses the chat
 * composer on thread mount, so a window-level letter binding would swallow the
 * first character of a message typed while the panel sits empty.
 */

import { ChevronRightIcon } from "lucide-react";

import { cn } from "~/lib/utils";

import { RIGHT_SIDEBAR_TAB_ICON } from "./RightSidebarShell.icons";
import type { AddableRightPanelKind } from "./rightPanelSurfaceTabs";
import type { RightPanelKind } from "../rightPanelSurface";

const ROW_SELECTOR = "[data-surface-launcher-card]:not([disabled])";

/** Arrow keys that roam the grid, mapped to the direction they step. */
const ROAM_STEP: Readonly<Record<string, 1 | -1>> = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};

export interface RightPanelSurfaceLauncherProps {
  /** Every surface with a tab, in strip order, including disabled ones. */
  readonly kinds: ReadonlyArray<AddableRightPanelKind>;
  readonly onSelect: (kind: RightPanelKind) => void;
}

/**
 * Move focus to the next or previous enabled row, wrapping at both ends.
 */
function focusAdjacentRow(list: HTMLElement, step: 1 | -1): void {
  const rows = Array.from(list.querySelectorAll<HTMLButtonElement>(ROW_SELECTOR));
  if (rows.length === 0) return;
  const current = rows.findIndex((row) => row === document.activeElement);
  const next = current === -1 ? (step === 1 ? 0 : rows.length - 1) : current + step;
  rows[(next + rows.length) % rows.length]?.focus();
}

export function RightPanelSurfaceLauncher(props: RightPanelSurfaceLauncherProps) {
  const { kinds, onSelect } = props;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
    const step = ROAM_STEP[event.key];
    if (step === undefined) return;
    event.preventDefault();
    focusAdjacentRow(event.currentTarget, step);
  };

  return (
    <div
      className="flex min-h-0 flex-1 overflow-y-auto p-2"
      data-testid="right-sidebar-surface-launcher"
    >
      <div className="m-auto w-full max-w-lg">
        <div className="px-1">
          <h3 className="font-medium text-foreground text-sm">Choose a surface</h3>
          <p className="mt-0.5 text-muted-foreground text-xs">Open it in the right sidebar.</p>
        </div>
        <div
          role="group"
          aria-label="Choose a surface"
          onKeyDown={handleKeyDown}
          className="mt-2 overflow-hidden rounded-[var(--control-radius)] border border-border bg-card"
        >
          {kinds.map((tab) => (
            <SurfaceLauncherRow key={tab.kind} tab={tab} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </div>
  );
}

interface SurfaceLauncherRowProps {
  readonly tab: AddableRightPanelKind;
  readonly onSelect: (kind: RightPanelKind) => void;
}

function SurfaceLauncherRow(props: SurfaceLauncherRowProps) {
  const { tab, onSelect } = props;
  const Icon = RIGHT_SIDEBAR_TAB_ICON[tab.kind];

  return (
    <button
      type="button"
      disabled={tab.disabled}
      data-surface-launcher-card=""
      data-testid={`right-sidebar-surface-launcher-card-${tab.kind}`}
      onClick={() => {
        onSelect(tab.kind);
      }}
      className={cn(
        "flex min-h-touch w-full items-center gap-2.5 border-b border-border px-3 text-left outline-none transition-colors last:border-b-0 md:min-h-10",
        tab.disabled
          ? "cursor-not-allowed bg-muted/20 opacity-50"
          : "cursor-pointer hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
      )}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className="shrink-0 font-medium text-sm text-foreground">{tab.label}</span>
        <span className="truncate text-muted-foreground text-xs">
          {tab.disabled ? (tab.disabledReason ?? tab.description) : tab.description}
        </span>
      </span>
      {tab.disabled ? null : (
        <ChevronRightIcon
          aria-hidden="true"
          className="size-3.5 shrink-0 text-muted-foreground/55"
        />
      )}
    </button>
  );
}
